import React from 'react';

export type IconGraphicVariant = 'default' | 'success' | 'error' | 'warning';
export type IconGraphicShape = 'circle' | 'rounded-square';
export type IconGraphicSize = 'small' | 'medium' | 'large';

interface IconGraphicProps {
  icon: React.ElementType;
  variant?: IconGraphicVariant;
  shape?: IconGraphicShape;
  size?: IconGraphicSize;
  isTransitioningToSuccess?: boolean;
}

// Animation keyframes
const _animations = {
  // Initial entrance animations
  mainEnter: {
    '@keyframes mainEnter': {
      '0%': {
        opacity: 0,
        transform: 'translateX(-30px) translateY(-20px) scale(0.8)',
      },
      '100%': {
        opacity: 1,
        transform: 'translateX(0) translateY(0) scale(1)',
      },
    },
  },
  secondaryEnter: {
    '@keyframes secondaryEnter': {
      '0%': {
        opacity: 0,
        transform: 'translateX(40px) translateY(30px) scale(0.6) rotate(15deg)',
      },
      '100%': {
        opacity: 1,
        transform: 'translateX(0) translateY(0) scale(1) rotate(15deg)',
      },
    },
  },
  // Idle floating animations
  mainFloat: {
    '@keyframes mainFloat': {
      '0%, 100%': {
        transform: 'translateY(0px)',
      },
      '25%': {
        transform: 'translateY(-2px)',
      },
      '50%': {
        transform: 'translateY(-4px)',
      },
      '75%': {
        transform: 'translateY(-2px)',
      },
    },
  },
  secondaryFloat: {
    '@keyframes secondaryFloat': {
      '0%, 100%': {
        transform: 'translateY(0px)',
      },
      '25%': {
        transform: 'translateY(-3px)',
      },
      '50%': {
        transform: 'translateY(-6px)',
      },
      '75%': {
        transform: 'translateY(-3px)',
      },
    },
  },
  secondaryFloatRotated: {
    '@keyframes secondaryFloatRotated': {
      '0%, 100%': {
        transform: 'rotate(15deg) translateY(0px)',
      },
      '25%': {
        transform: 'rotate(15deg) translateY(-3px)',
      },
      '50%': {
        transform: 'rotate(15deg) translateY(-6px)',
      },
      '75%': {
        transform: 'rotate(15deg) translateY(-3px)',
      },
    },
  },
  // Color transition animations
  colorTransition: {
    '@keyframes colorTransition': {
      '0%': {
        transform: 'scale(1)',
      },
      '50%': {
        transform: 'scale(1.1)',
      },
      '100%': {
        transform: 'scale(1)',
      },
    },
  },
};

const VARIANT_COLORS = {
  default: {
    background: '#4C6EF53B',
    iconColor: '#FFFFFF95',
    iconShadow: '#4C6EF5',
    solidColor: '#4C6EF5',
  },
  success: {
    background: '#15B79E3B',
    iconColor: '#FFFFFF95',
    iconShadow: '#15B79E',
    solidColor: '#15B79E',
  },
  error: {
    background: '#F443363B',
    iconColor: '#FFFFFF95',
    iconShadow: '#F44336',
    solidColor: '#F44336',
  },
  warning: {
    background: '#FF98003B',
    iconColor: '#FFFFFF95',
    iconShadow: '#FF9800',
    solidColor: '#FF9800',
  },
} as const;

const SIZE_CONFIG = {
  small: {
    container: { width: 100, height: 80 },
    main: { width: 80, height: 80 },
    secondary: { width: 60, height: 60 },
    icon: 40,
    offset: { top: -8, right: 8 },
  },
  medium: {
    container: { width: 150, height: 120 },
    main: { width: 120, height: 120 },
    secondary: { width: 90, height: 90 },
    icon: 64,
    offset: { top: -10, right: 10 },
  },
  large: {
    container: { width: 200, height: 160 },
    main: { width: 160, height: 160 },
    secondary: { width: 120, height: 120 },
    icon: 80,
    offset: { top: -12, right: 12 },
  },
} as const;

const getShapeStyles = (shape: IconGraphicShape) => {
  switch (shape) {
    case 'circle':
      return { borderRadius: '50%' };
    case 'rounded-square':
      return { borderRadius: '25%' };
    default:
      return { borderRadius: '50%' };
  }
};

const getSecondaryShapeStyles = (shape: IconGraphicShape) => {
  switch (shape) {
    case 'circle':
      return { borderRadius: '50%' };
    case 'rounded-square':
      return {
        borderRadius: '25%',
        transform: 'rotate(15deg)',
      };
    default:
      return { borderRadius: '50%' };
  }
};

const getSecondaryShapePosition = (shape: IconGraphicShape, sizeConfig: any) => {
  if (shape === 'rounded-square') {
    // Adjust position for rotated square - move it up a bit
    return {
      top: sizeConfig.offset.top - 15, // Move 15px higher
      right: sizeConfig.offset.right,
    };
  }

  // Default position for circles
  return {
    top: sizeConfig.offset.top,
    right: sizeConfig.offset.right,
  };
};

const getSecondaryShapeBackground = (variant: IconGraphicVariant) => {
  switch (variant) {
    case 'default':
      return `linear-gradient(180deg, #4C6EF5 0%, #1E2C62 100%)`;
    case 'success':
      return `linear-gradient(180deg, #78F2B7 0%,  #15B79E 100%)`;
    case 'error':
      return `linear-gradient(180deg, #FF8A80 0%, #F44336 100%)`;
    case 'warning':
      return `linear-gradient(180deg, #FFD54F 0%, #FF9800 100%)`;
    default:
      return `linear-gradient(180deg, #4C6EF5 0%, #1E2C62 100%)`;
  }
};

const getMainShapeBackground = (variant: IconGraphicVariant) => {
  switch (variant) {
    case 'default':
      return `linear-gradient(180deg, #4C6EF53B 0%, #1E2C621B 100%)`;
    case 'success':
      return `linear-gradient(180deg, #78F2B73B 0%, #15B79E1B 100%)`;
    case 'error':
      return `linear-gradient(180deg, #FF8A803B 0%, #F443361B 100%)`;
    case 'warning':
      return `linear-gradient(180deg, #FFD54F3B 0%, #FF98001B 100%)`;
    default:
      return `linear-gradient(180deg, #4C6EF53B 0%, #1E2C621B 100%)`;
  }
};

export const IconGraphic: React.FC<IconGraphicProps> = ({
  icon: IconComponent,
  variant = 'default',
  shape = 'circle',
  size = 'medium',
  isTransitioningToSuccess = false,
}) => {
  // Use success variant if transitioning or if already success
  const effectiveVariant = isTransitioningToSuccess ? 'success' : variant;
  const colors = VARIANT_COLORS[effectiveVariant];
  const sizeConfig = SIZE_CONFIG[size];
  const shapeStyles = getShapeStyles(shape);
  const secondaryShapeStyles = getSecondaryShapeStyles(shape);
  const secondaryPosition = getSecondaryShapePosition(shape, sizeConfig);
  const secondaryBackground = getSecondaryShapeBackground(effectiveVariant);
  const mainBackground = getMainShapeBackground(effectiveVariant);

  // Build keyframe CSS string
  const keyframesStyle = `
    @keyframes mainEnter { 0% { opacity: 0; transform: translateX(-30px) translateY(-20px) scale(0.8); } 100% { opacity: 1; transform: translateX(0) translateY(0) scale(1); } }
    @keyframes secondaryEnter { 0% { opacity: 0; transform: translateX(40px) translateY(30px) scale(0.6) rotate(15deg); } 100% { opacity: 1; transform: translateX(0) translateY(0) scale(1) rotate(15deg); } }
    @keyframes mainFloat { 0%, 100% { transform: translateY(0px); } 25% { transform: translateY(-2px); } 50% { transform: translateY(-4px); } 75% { transform: translateY(-2px); } }
    @keyframes secondaryFloat { 0%, 100% { transform: translateY(0px); } 25% { transform: translateY(-3px); } 50% { transform: translateY(-6px); } 75% { transform: translateY(-3px); } }
    @keyframes secondaryFloatRotated { 0%, 100% { transform: rotate(15deg) translateY(0px); } 25% { transform: rotate(15deg) translateY(-3px); } 50% { transform: rotate(15deg) translateY(-6px); } 75% { transform: rotate(15deg) translateY(-3px); } }
    @keyframes colorTransition { 0% { transform: scale(1); } 50% { transform: scale(1.1); } 100% { transform: scale(1); } }
  `;

  return (
    <>
      <style>{keyframesStyle}</style>
      <div
        style={{
          position: 'relative',
          width: sizeConfig.container.width,
          height: sizeConfig.container.height,
        }}
      >
        {/* Main shape with glassmorphism effect */}
        <div
          style={{
            width: sizeConfig.main.width,
            height: sizeConfig.main.height,
            background: mainBackground,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            boxShadow: 'inset 0 0 1px rgba(255, 255, 255, 0.3)',
            opacity: 0,
            willChange: 'transform',
            transition: 'background 0.6s ease-in-out, box-shadow 0.6s ease-in-out',
            animation: `mainEnter 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, mainFloat 6s ease-in-out 1s infinite${
              isTransitioningToSuccess && variant !== 'success' ? ', colorTransition 0.6s ease-in-out' : ''
            }`,
            ...shapeStyles,
          }}
        >
          <IconComponent
            size={sizeConfig.icon}
            style={{
              color: 'transparent',
              stroke: colors.iconColor,
              filter: `drop-shadow(0px 4px 4px ${colors.iconShadow})`,
              willChange: 'transform',
            }}
          />
        </div>

        {/* Secondary smaller shape */}
        <div
          style={{
            position: 'absolute',
            width: sizeConfig.secondary.width,
            height: sizeConfig.secondary.height,
            background: secondaryBackground,
            top: secondaryPosition.top,
            right: secondaryPosition.right,
            opacity: 0,
            willChange: 'transform',
            transition: 'background 0.6s ease-in-out',
            animation:
              shape === 'rounded-square'
                ? 'secondaryEnter 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s forwards, secondaryFloatRotated 7s ease-in-out 1.5s infinite'
                : 'secondaryEnter 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s forwards, secondaryFloat 7s ease-in-out 1.5s infinite',
            ...secondaryShapeStyles,
          }}
        />
      </div>
    </>
  );
};
